import { inject } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

import { AgentRelayClient, AgentUsage } from './agent-relay.client';
import { AgentAction } from 'src/app/@shared/models/ai/agent-action.model';
import { AgentToolCall } from 'src/app/@shared/models/ai/agent-tool-call.model';
import { AgentTurn, AgentTurnFeedback } from 'src/app/@shared/models/ai/agent-turn.model';
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';

export interface ConversationMessage {
    role: 'user' | 'agent';
    content: string;
    /** Résumé de raisonnement (messages agent) — affiché en direct puis replié. */
    reasoning?: string;
    /** Consommation de tokens + coût brut de CE tour (event `usage`). */
    usage?: AgentUsage;
    /** Journal des appels d'outils du tour (historique rejoué, supervision §9.1). */
    toolCalls?: AgentToolCall[];
    /** Écritures de l'agent pendant le tour (#1217) : cartes sous la dernière bulle du tour. */
    actions?: AgentAction[];
    /** Index du tour côté serveur (bulle qui porte les cartes) : de quoi l'annuler. */
    turnIndex?: number;
    /** Il reste quelque chose à annuler dans le tour : bouton « Annuler cet échange » sous ses cartes. */
    canUndoTurn?: boolean;
    /** Avis donné sur la réponse du tour (#1284) ; porté, comme `turnIndex`, par la dernière bulle du tour. */
    feedback?: AgentTurnFeedback | null;
    feedbackComment?: string;
}

/** Texte affiché pendant que l'agent travaille, avant/entre les fragments de réponse. */
const THINKING_LABEL = 'Réflexion';

/**
 * Base d'un agent conversationnel en mode relay.
 *
 * Le serveur est source de vérité de l'historique : le front n'envoie que le
 * dernier message et ne conserve que le `sessionId` de la conversation. Le fil
 * affiché est reconstruit localement (au fil du flux SSE, ou rejoué via une
 * session existante).
 */
export abstract class AgentServiceBase {

    private _relay = inject(AgentRelayClient);

    private _turnSubscription: Subscription | null = null;

    /*----------------------------------------------------------------------------------------
     * À implémenter par les sous-classes
     ----------------------------------------------------------------------------------------*/

    /** Suffixe d'agent pour une nouvelle conversation (résout le Prompt `live_<agent>`). */
    protected abstract get agentName(): string;

    /*----------------------------------------------------------------------------------------
     * Observables exposés
     ----------------------------------------------------------------------------------------*/

    private _conversationMessages$ = new BehaviorSubject<ConversationMessage[]>([]);
    /** Fil de conversation local de cet agent. */
    readonly conversationMessages$ = this._conversationMessages$.asObservable();

    private _streaming$ = new BehaviorSubject<boolean>(false);
    /** Vrai tant qu'un tour est en cours de réception. */
    readonly streaming$ = this._streaming$.asObservable();

    private _statusText$ = new BehaviorSubject<string | null>(null);
    /**
     * Libellé d'activité transitoire (« Réflexion », ou libellé d'outil pendant
     * son exécution). `null` quand l'agent n'est pas en train de travailler ou
     * qu'il streame déjà sa réponse texte.
     */
    readonly statusText$ = this._statusText$.asObservable();

    private _sessionId$ = new BehaviorSubject<string | null>(null);
    /** Id de la conversation courante côté serveur (`null` = nouvelle conversation). */
    readonly sessionId$ = this._sessionId$.asObservable();

    private _sessionUsage$ = new BehaviorSubject<AgentUsage | null>(null);
    /**
     * Cumul de tokens + coût brut de la session (event `usage`). `null` tant
     * qu'aucun tour n'a été streamé : l'historique rejoué ne porte pas de tokens.
     */
    readonly sessionUsage$ = this._sessionUsage$.asObservable();

    get streaming(): boolean {
        return this._streaming$.value;
    }

    get sessionId(): string | null {
        return this._sessionId$.value;
    }

    /*----------------------------------------------------------------------------------------
     * Contrôles de conversation
     ----------------------------------------------------------------------------------------*/

    /** Envoie une saisie utilisateur et démarre un tour. Ignoré si un tour est en cours. */
    sendText(text: string): void {
        const trimmed = text.trim();
        if (!trimmed || this._streaming$.value) return;

        this._appendMessage({ role: 'user', content: trimmed });
        this._startTurn(trimmed);
    }

    /** Charge une conversation existante (rejeu des tours) et la rend active. */
    loadSession(id: string, turns: AgentTurn[], sessionUsage: AgentUsage | null = null): void {
        this._resetTurn();
        this._sessionId$.next(id);
        // L'historique persiste désormais la conso : on réaffiche le cumul de la session.
        this._sessionUsage$.next(sessionUsage);
        const messages: ConversationMessage[] = turns
            .sort((a, b) => a.turnIndex - b.turnIndex)
            .flatMap(turn => [
                { role: 'user' as const, content: turn.userMessage },
                {
                    role: 'agent' as const,
                    content: turn.agentMessage,
                    // Réflexion (supervision only) : même zone repliable qu'en live.
                    reasoning: turn.agentThoughts || undefined,
                    usage: turn.usage ?? undefined,
                    toolCalls: turn.toolCalls.length ? turn.toolCalls : undefined,
                    actions: turn.actions.length ? turn.actions : undefined,
                    turnIndex: turn.turnIndex,
                    canUndoTurn: turn.canUndo,
                    feedback: turn.feedback,
                    feedbackComment: turn.feedbackComment,
                },
            ]);
        this._conversationMessages$.next(messages);
    }

    /** Reporte dans le fil l'avis enregistré par le serveur sur la réponse d'un tour (#1284). */
    setTurnFeedback(turnIndex: number, feedback: AgentTurnFeedback | null, feedbackComment: string): void {
        this._conversationMessages$.next(this._conversationMessages$.value.map(message =>
            message.role === 'agent' && message.turnIndex === turnIndex ? { ...message, feedback, feedbackComment } : message
        ));
    }

    /** Vide le fil, oublie la session et annule le tour en cours (« nouvelle conversation »). */
    clear(): void {
        this._resetTurn();
        this._sessionId$.next(null);
        this._sessionUsage$.next(null);
        this._conversationMessages$.next([]);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _startTurn(message: string): void {
        this._streaming$.next(true);
        this._statusText$.next(THINKING_LABEL);

        // La bulle assistant courante reste le dernier message tant qu'aucun outil
        // n'a démarré ; un `tool` start la « ferme » et un nouveau token en rouvre une.
        let assistantOpen = false;

        // Cartes des écritures du tour, toujours sous sa dernière bulle — comme au rejeu,
        // où le tour n'a qu'une bulle. `turnStart` est l'index du message utilisateur.
        const turnStart = this._conversationMessages$.value.length - 1;
        const turnActions: AgentAction[] = [];
        let turnIndex: number | null = null;
        // Erreur de transport : le tour n'est pas enregistré, il ne peut recevoir aucun avis.
        let transportFailed = false;

        this._turnSubscription = this._relay
            .chat$({ agent: this.agentName, sessionId: this._sessionId$.value, message })
            .subscribe({ next: event => {
                switch (event.type) {

                    case 'session':
                        this._sessionId$.next(event.sessionId);
                        turnIndex = event.turnIndex ?? null;
                        break;

                    case 'token':
                        this._statusText$.next(null);
                        if (assistantOpen) {
                            this._appendToLastContent(event.text);
                        } else {
                            assistantOpen = true;
                            this._appendMessage({ role: 'agent', content: event.text });
                            this._attachTurnActions(turnStart, turnActions);
                        }
                        break;

                    case 'thought':
                        // Raisonnement affiché en direct dans la bulle agent (replié dès
                        // que la réponse arrive). On masque le label transitoire au
                        // profit du texte live.
                        this._statusText$.next(null);
                        if (assistantOpen) {
                            this._appendToLastReasoning(event.text);
                        } else {
                            assistantOpen = true;
                            this._appendMessage({ role: 'agent', content: '', reasoning: event.text });
                            this._attachTurnActions(turnStart, turnActions);
                        }
                        break;

                    case 'tool':
                        // Indicateur d'activité transitoire : le libellé fourni par le serveur.
                        assistantOpen = false;
                        this._statusText$.next(event.phase === 'start' ? event.label : THINKING_LABEL);
                        break;

                    case 'action': {
                        // Écriture de l'agent : sa carte, avec Annuler / Conserver.
                        const { type, ...card } = event;
                        turnActions.push(GeneralFactory.get('AgentAction', card) as AgentAction);
                        this._attachTurnActions(turnStart, turnActions);
                        break;
                    }

                    case 'usage':
                        // Émis une fois par tour, juste avant `done` : on rattache le
                        // détail du tour à la dernière bulle agent et on met à jour le
                        // cumul de session.
                        this._setLastUsage(turnStart, event.turn);
                        this._sessionUsage$.next(event.session);
                        break;

                    case 'done':
                        this._statusText$.next(null);
                        this._streaming$.next(false);
                        break;

                    case 'error':
                        if (event.outcome) {
                            // Tour en échec (limite d'outils, erreur technique) : le message est
                            // la réponse de l'agent, persistée en fin de tour — il rejoint la
                            // bulle du tour et les cartes redescendent sous lui, comme au rejeu.
                            this._appendTurnFailure(turnStart, event.message, turnActions);
                        } else {
                            // Erreur de transport (HTTP, réseau) : rien n'est enregistré côté
                            // serveur, l'avertissement reste une bulle à part.
                            transportFailed = true;
                            this._appendMessage({ role: 'agent', content: `⚠️ ${event.message}` });
                        }
                        this._statusText$.next(null);
                        this._streaming$.next(false);
                        break;
                }
            }, complete: () => {
                // Le serveur n'enregistre le tour qu'à la fermeture du flux, après `done` :
                // l'avis sur la réponse et « Annuler cet échange » ne se proposent qu'à ce moment,
                // ce dernier si une carte s'y prête.
                if (turnIndex === null) return;
                if (!transportFailed) {
                    this._markTurnAnswered(turnStart, turnIndex);
                }
                if (turnActions.some(action => action.canUndo || action.canReject)) {
                    this._markTurnUndoable(turnStart, turnIndex);
                }
            } });
    }

    private _resetTurn(): void {
        this._turnSubscription?.unsubscribe();
        this._turnSubscription = null;
        this._streaming$.next(false);
        this._statusText$.next(null);
    }

    private _appendMessage(message: ConversationMessage): void {
        this._conversationMessages$.next([...this._conversationMessages$.value, message]);
    }

    /** Concatène du contenu (réponse) au dernier message et réémet la liste. */
    private _appendToLastContent(content: string): void {
        const current = this._conversationMessages$.value;
        if (!current.length) return;
        const last = current[current.length - 1];
        const updated = { ...last, content: last.content + content };
        this._conversationMessages$.next([...current.slice(0, -1), updated]);
    }

    /** Concatène du raisonnement au dernier message et réémet la liste. */
    private _appendToLastReasoning(reasoning: string): void {
        const current = this._conversationMessages$.value;
        if (!current.length) return;
        const last = current[current.length - 1];
        const updated = { ...last, reasoning: (last.reasoning ?? '') + reasoning };
        this._conversationMessages$.next([...current.slice(0, -1), updated]);
    }

    /**
     * Écrit le message d'échec du tour dans sa dernière bulle agent — après ce qui y est
     * déjà, séparé d'une ligne vide, comme le serveur le persiste — ou en ouvre une, puis y
     * redescend les cartes : le direct montre le tour comme le rejeu le montrera.
     */
    private _appendTurnFailure(turnStart: number, message: string, actions: AgentAction[]): void {
        const current = this._conversationMessages$.value;
        const lastAgentIndex = current.map(m => m.role).lastIndexOf('agent');
        if (lastAgentIndex <= turnStart) {
            this._appendMessage({ role: 'agent', content: message });
        } else {
            const last = current[lastAgentIndex];
            const updated = { ...last, content: last.content ? `${last.content}\n\n${message}` : message };
            this._conversationMessages$.next([
                ...current.slice(0, lastAgentIndex),
                updated,
                ...current.slice(lastAgentIndex + 1),
            ]);
        }
        this._attachTurnActions(turnStart, actions);
    }

    /**
     * Rattache les cartes du tour à sa dernière bulle agent, et les retire des bulles
     * précédentes. Sans bulle agent encore ouverte dans le tour, une bulle vide les porte :
     * la réponse qui suit en ouvre une nouvelle, où elles redescendent.
     */
    private _attachTurnActions(turnStart: number, actions: AgentAction[]): void {
        if (!actions.length) return;
        const current = this._conversationMessages$.value;
        const lastAgentIndex = current.map(m => m.role).lastIndexOf('agent');
        if (lastAgentIndex <= turnStart) {
            this._appendMessage({ role: 'agent', content: '', actions: [...actions] });
            return;
        }
        this._conversationMessages$.next(current.map((message, index) => {
            if (index === lastAgentIndex) return { ...message, actions: [...actions] };
            if (index > turnStart && message.actions) return { ...message, actions: undefined };
            return message;
        }));
    }

    /**
     * Désigne le tour sur sa dernière bulle agent, comme au rejeu : c'est ce qui ouvre l'avis
     * sur la réponse (#1284). Sans bulle agent dans le tour, il n'y a rien à noter.
     */
    private _markTurnAnswered(turnStart: number, turnIndex: number): void {
        const current = this._conversationMessages$.value;
        const index = current.map(message => message.role).lastIndexOf('agent');
        if (index <= turnStart) return;
        this._conversationMessages$.next(current.map((message, i) =>
            i === index ? { ...message, turnIndex, feedback: null, feedbackComment: '' } : message
        ));
    }

    /** Pose « Annuler cet échange » sur la bulle qui porte les cartes du tour, comme au rejeu. */
    private _markTurnUndoable(turnStart: number, turnIndex: number): void {
        const current = this._conversationMessages$.value;
        const index = current.map(message => !!message.actions?.length).lastIndexOf(true);
        if (index <= turnStart) return;
        this._conversationMessages$.next(current.map((message, i) =>
            i === index ? { ...message, turnIndex, canUndoTurn: true } : message
        ));
    }

    /**
     * Rattache le détail d'`usage` du tour à sa dernière bulle agent. Sans bulle encore
     * ouverte dans le tour — limite d'outils atteinte avant le premier mot —, une bulle
     * vide le porte, que le message d'échec qui suit vient remplir : il ne rejoint jamais
     * le tour précédent.
     */
    private _setLastUsage(turnStart: number, usage: AgentUsage): void {
        const current = this._conversationMessages$.value;
        const lastAgentIndex = current.map(m => m.role).lastIndexOf('agent');
        if (lastAgentIndex <= turnStart) {
            this._appendMessage({ role: 'agent', content: '', usage });
            return;
        }
        const updated = { ...current[lastAgentIndex], usage };
        this._conversationMessages$.next([
            ...current.slice(0, lastAgentIndex),
            updated,
            ...current.slice(lastAgentIndex + 1),
        ]);
    }
}
